import React from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';
import { reduxForm, getValues } from 'redux-form';
import { v4 } from 'uuid';

import TrustPilotBadge from '@src/components/TrustPilotBadge/TrustPilotBadge';
import { getSplitTestValue } from '@src/utils/selectors';

import { limitedCompanyPersonalInformationFields as fields } from './fields';
import Person from './Person';
import { signUpLimitedCompanyPersonalInformationSchema } from './schema';
import schemaValidator from './validate';
import { toggleManualAddress } from '../../actions';
import CompanyDirectorNotice from '../../components/CompanyDirectorNotice';
import FinishStepButton from '../../components/FinishStepButton/FinishStepButton';
import applicationSchema from '../../helpers/applicationSchema';
import { scrollToErrors } from '../../helpers/scrollToErrors';
import { userIsIndicativeOfferFlow } from '../../helpers/user';
import {
    hasAutomatedDecision as hasAutomatedDecisionSelector,
    getSuggestedProductType,
    getRequestedProductType,
    getCountryCode
} from '../../lendingAPI/lendingAPISelector';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';

class LimitedCompanyInformation extends React.Component {
    UNSAFE_componentWillReceiveProps(nextProps) {
        const {
            fields: { people }
        } = nextProps;
        const applicant = _.find(people, (person) =>
            _.includes(person.roles.value, 'applicant')
        );
        if (people.length && !applicant) {
            let emptyPerson = _.first(applicationSchema.people);
            people.addField(emptyPerson);
        }
    }

    onStatusSelect = (status) => {
        const { people } = this.props.fields;
        const val = (status && status.value) || '';

        let person = _.find(people, (person) => {
            return _.includes(person.roles.value, 'applicant');
        });
        person.addresses[0].residential_status.onChange(val);
    };

    onAddressSelect = (addressIndex, address) => {
        const { people } = this.props.fields;
        const { people: peopleValues } = this.props.values;
        const personFields = _.find(people, (person) => {
            return _.includes(person.roles.value, 'applicant');
        });
        const personValues = _.find(peopleValues, (person) => {
            return _.includes(person.roles, 'applicant');
        });
        let newAddress = _.assign(
            {},
            personValues.addresses[addressIndex],
            address
        );

        if (_.isEmpty(address)) {
            let emptyAddress = {
                house_name: '',
                house_number: '',
                street_line_1: '',
                town: '',
                postcode: '',
                equifax_token: ''
            };
            _.assign(newAddress, emptyAddress);
        }
        personFields.addresses.removeField(addressIndex);
        personFields.addresses.addField(newAddress, addressIndex);
    };

    onMonthSelect = (month) => {
        const { people } = this.props.fields;
        let val = (month && month.value) || '';
        let person = _.find(people, (person) => {
            return _.includes(person.roles.value, 'applicant');
        });
        tracking.ampli.formFieldCompleted({
            form_field: 'dob_month'
        });
        person.date_of_birth.mm.onChange(val);
    };

    onManualAddressToggle = (personUID, addressIndex, e) => {
        e && e.preventDefault();
        this.props.toggleManualAddress(personUID, addressIndex);
    };

    render() {
        const {
            fields: { people },
            ui: { manualAddresses },
            fetched,
            fetched: { addresses: fetchedAddresses },
            onCountrySelect,
            fetchAddressLookup,
            handleSubmit,
            nextStep,
            isInIndicativeOfferFlow,
            suggestedProductKey,
            requestedProductType,
            isAutoAgreedPrivacyPolicy
        } = this.props;

        const applicant = _.find(people, (person) => {
            return _.includes(person.roles.value, 'applicant');
        });

        const applicantIndex = _.findIndex(people, (person) =>
            _.includes(person.roles.value, 'applicant')
        );

        /**
         * Assign's a date to the privacy_policy_agreement_date if there is none.
         * Assign's a guarantor role if user is a guarantor.
         */
        const setPrivacyPolicy = () => {
            const { privacy_policy_agreement_date, roles } = applicant;

            if (privacy_policy_agreement_date.value === null) {
                // TODO: review this line to see if it's required
                let customerRoles = roles.value.filter(
                    (role) => role !== 'guarantor'
                );
                customerRoles = roles.value.concat('guarantor');

                roles.onChange?.(_.uniq(customerRoles));

                const date = new Date().toISOString();
                privacy_policy_agreement_date?.onChange?.(date);
            }
        };

        return (
            <form
                noValidate
                autoComplete="off"
                className="form-horizontal"
                onSubmit={handleSubmit(nextStep)}>
                <div className="form-wrapper">
                    <CompanyDirectorNotice />
                    {applicant ? (
                        <Person
                            fetchedDirectors={fetched.directors}
                            toggleManualAddress={this.onManualAddressToggle}
                            onAddressChange={fetchAddressLookup}
                            onAddressSelect={this.onAddressSelect}
                            onMonthSelect={this.onMonthSelect}
                            onStatusSelect={this.onStatusSelect}
                            person={applicant}
                            personIndex={applicantIndex}
                            fetchedAddresses={fetchedAddresses}
                            manualAddressesState={manualAddresses}
                            formLayout="ltd"
                            onCountrySelect={onCountrySelect}
                            isInIndicativeOfferFlow={isInIndicativeOfferFlow}
                        />
                    ) : null}
                </div>

                <FinishStepButton
                    type="submit"
                    onClick={() => {
                        // ampli tracking:
                        tracking.ampli.instantDecisionCtaClicked({
                            suggestProductType: suggestedProductKey,
                            requestedProductType,
                            ...getAmplitudeDefaultEventProperties()
                        });

                        tracking.ampli.formFieldCompleted({
                            form_field: 'privacy_policy'
                        });

                        // errors
                        scrollToErrors(this.props.errors);

                        if (isAutoAgreedPrivacyPolicy) {
                            setPrivacyPolicy();
                        }
                    }}
                    explainerText="Applying won't affect your credit score">
                    Submit details
                </FinishStepButton>
                <TrustPilotBadge />
            </form>
        );
    }
}

const mapStateToProps = (state) => ({
    ui: state.ui,
    fetched: state.fetched,
    formValues: getValues(state.form.sign_up),
    countryCode: getCountryCode(state),
    isAutoAgreedPrivacyPolicy:
        getSplitTestValue(state, 'privacy_policy_default_agreement_test') &&
        getCountryCode(state) === 'uk',
    initialValues: {
        people: _.chain(state.fetched.application.payload.people)
            .filter((person) => _.includes(person.roles, 'applicant'))
            .map((person) => {
                return {
                    ...person,
                    roles: _.uniq([...person.roles, 'director'])
                };
            })
            .map((person) =>
                _.merge({}, _.first(applicationSchema.people), person)
            )
            .map((person) => {
                if (!_.find(person.phone, { type: 'main' })) {
                    person.phone = person.phone.concat({
                        type: 'main',
                        number: '',
                        uid: v4()
                    });
                }
                if (!_.find(person.phone, { type: 'secondary' })) {
                    person.phone = person.phone.concat({
                        type: 'secondary',
                        number: '',
                        uid: v4()
                    });
                }
                person.phone = _.uniq(person.phone, (phone) => phone.type);
                return person;
            })
            .value()
    },
    isInIndicativeOfferFlow: userIsIndicativeOfferFlow(state.ui),
    hasAutomatedDecision: hasAutomatedDecisionSelector(state),
    suggestedProductKey: getSuggestedProductType(state),
    requestedProductType: getRequestedProductType(state)
});

const mapDispatchToProps = (dispatch) => ({
    toggleManualAddress: (personUID, addressIndex) =>
        dispatch(toggleManualAddress(personUID, addressIndex)),
    fetchAddressLookup: (postcode) =>
        dispatch({ type: 'ADDRESSES_FETCH_REQUESTED', postcode })
});

export default reduxForm(
    {
        form: 'sign_up',
        fields,
        destroyOnUnmount: false,
        getFormState: (state, reduxMountPoint) => state[reduxMountPoint],
        asyncValidate: schemaValidator(
            signUpLimitedCompanyPersonalInformationSchema
        ),
        alwaysAsyncValidate: true,
        asyncBlurFields: _.without(
            fields,
            'people[].privacy_policy_agreement_date'
        )
    },
    mapStateToProps,
    mapDispatchToProps
)(LimitedCompanyInformation);
